import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  Alert,
  Text,
  Modal,
  Pressable,
  Animated,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '@/providers/ThemeProvider';
import { useChatActions } from '../hooks/useChatActions';
import { useTypingIndicator } from '../hooks/useTypingIndicator';
import { Message } from '../types';

interface MessageInputProps {
  chatId: string;
  userId: string;
  onSendSuccess?: () => void;
  onMessageSent?: (message: any) => void;
  onPollPress?: () => void;
  /** Message being replied to */
  replyToMessage?: Message | null;
  /** Called to clear the reply */
  onCancelReply?: () => void;
}

interface AttachmentOption {
  id: string;
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  color: string;
  onPress: () => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  chatId,
  userId,
  onSendSuccess,
  onMessageSent,
  onPollPress,
  replyToMessage,
  onCancelReply,
}) => {
  const insets = useSafeAreaInsets();
  const colors = useThemeColors();

  const [message, setMessage] = useState('');
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const rotateAnim = useRef(new Animated.Value(0)).current;

  const { sendMessage, sendImage, sendFile, sending, uploading } = useChatActions(chatId, userId);
  const { setTyping, stopTyping } = useTypingIndicator(chatId, userId);

  // Auto-focus input when replying
  useEffect(() => {
    if (replyToMessage) {
      inputRef.current?.focus();
    }
  }, [replyToMessage]);

  const toggleAttachmentMenu = () => {
    const toValue = showAttachmentMenu ? 0 : 1;
    Animated.spring(rotateAnim, {
      toValue,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();
    setShowAttachmentMenu(!showAttachmentMenu);
  };

  const closeMenu = () => {
    Animated.spring(rotateAnim, {
      toValue: 0,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();
    setShowAttachmentMenu(false);
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;

    const messageText = message.trim();
    const replyId = replyToMessage?.id;
    setMessage('');
    onCancelReply?.();

    try {
      const sentMessage = await sendMessage({
        chat_id: chatId,
        content: messageText,
        type: 'text',
        reply_to_id: replyId,
      });

      stopTyping();

      // Immediately add the message to the list (optimistic update)
      if (sentMessage) {
        onMessageSent?.(sentMessage);
      }

      onSendSuccess?.();
    } catch (error) {
      Alert.alert('Error', 'Failed to send message');
      setMessage(messageText);
    }
  };

  const handleChangeText = (text: string) => {
    setMessage(text);

    if (text.length > 0) {
      setTyping();
    } else {
      stopTyping();
    }
  };

  const handleSendImage = async () => {
    closeMenu();
    try {
      const sentMessage = await sendImage();
      if (sentMessage) {
        onMessageSent?.(sentMessage);
      }
      onSendSuccess?.();
    } catch (error) {
      if (error instanceof Error && error.message !== 'Permission to access media library denied') {
        Alert.alert('Error', 'Failed to send image');
      }
    }
  };

  const handleSendFile = async () => {
    closeMenu();
    try {
      const sentMessage = await sendFile();
      if (sentMessage) {
        onMessageSent?.(sentMessage);
      }
      onSendSuccess?.();
    } catch (error) {
      Alert.alert('Error', 'Failed to send file');
    }
  };

  const handlePollPress = () => {
    closeMenu();
    onPollPress?.();
  };

  const attachmentOptions: AttachmentOption[] = [
    {
      id: 'image',
      icon: 'image-outline',
      label: 'Foto',
      color: '#10B981',
      onPress: handleSendImage,
    },
    {
      id: 'file',
      icon: 'document-outline',
      label: 'Bestand',
      color: '#3B82F6',
      onPress: handleSendFile,
    },
    ...(onPollPress
      ? [
          {
            id: 'poll',
            icon: 'bar-chart-outline' as keyof typeof Ionicons.glyphMap,
            label: 'Poll',
            color: '#7C3AED',
            onPress: handlePollPress,
          },
        ]
      : []),
  ];

  const rotation = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '45deg'],
  });

  return (
    <View
      className="px-4"
      style={{ 
        backgroundColor: colors.surface, 
        borderTopWidth: 1, 
        borderTopColor: colors.border,
        paddingTop: 8,
        paddingBottom: insets.bottom > 0 ? insets.bottom : 8,
      }}
    >
      {/* Reply preview bar */}
      {replyToMessage && (
        <View
          className="flex-row items-center mb-2 rounded-xl overflow-hidden"
          style={{ backgroundColor: colors.surfaceSecondary }}
        >
          {/* Accent bar */}
          <View style={{ width: 4, backgroundColor: colors.primary, alignSelf: 'stretch' }} />

          <View className="flex-1 px-3 py-2">
            <Text
              className="text-xs font-semibold"
              style={{ color: colors.primary }}
              numberOfLines={1}
            >
              {replyToMessage.sender?.nickname || replyToMessage.sender?.full_name || 'Onbekend'}
            </Text>
            <Text
              className="text-xs mt-0.5"
              style={{ color: colors.textSecondary }}
              numberOfLines={1}
            >
              {replyToMessage.type === 'image'
                ? '📷 Foto'
                : replyToMessage.type === 'file'
                  ? '📎 Bestand'
                  : replyToMessage.type === 'poll'
                    ? '📊 Poll'
                    : replyToMessage.content || 'Bericht'}
            </Text>
          </View>

          <TouchableOpacity
            onPress={onCancelReply}
            className="px-3 py-2"
            activeOpacity={0.7}
            accessibilityLabel="Annuleer antwoord"
            accessibilityRole="button"
          >
            <Ionicons name="close" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}

      <View className="flex-row items-center gap-2">
        {/* Attachment button with + icon */}
        <Animated.View style={{ transform: [{ rotate: rotation }] }}>
          <TouchableOpacity
            onPress={toggleAttachmentMenu}
            disabled={uploading}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary }}
            activeOpacity={0.7}
            accessibilityLabel="Bijlage toevoegen"
            accessibilityRole="button"
          >
            <Ionicons name="add" size={26} color="#FFFFFF" />
          </TouchableOpacity>
        </Animated.View>

        {/* Text input */}
        <View
          className="flex-1 rounded-3xl px-4 max-h-32 justify-center"
          style={{ backgroundColor: colors.surfaceSecondary, minHeight: 44 }}
        >
          <TextInput
            ref={inputRef}
            value={message}
            onChangeText={handleChangeText}
            placeholder="Typ een bericht..."
            placeholderTextColor={colors.textTertiary}
            multiline
            className="text-base"
            style={{ color: colors.text, maxHeight: 100, paddingTop: 0, paddingBottom: 0 }}
          />
        </View>

        {/* Send button */}
        <TouchableOpacity
          onPress={handleSend}
          disabled={!message.trim() || sending || uploading}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ 
            backgroundColor: message.trim() && !sending && !uploading 
              ? colors.primary 
              : colors.surfaceSecondary 
          }}
          activeOpacity={0.7}
        >
          <Ionicons
            name={sending ? 'hourglass-outline' : 'send'}
            size={20}
            color={message.trim() && !sending && !uploading ? '#fff' : colors.textTertiary}
          />
        </TouchableOpacity>
      </View>

      {/* Uploading indicator */}
      {uploading && (
        <View className="mt-2">
          <Text className="text-sm" style={{ color: colors.textSecondary }}>Uploaden...</Text>
        </View>
      )}

      {/* Attachment Menu Modal */}
      <Modal
        visible={showAttachmentMenu}
        transparent
        animationType="fade"
        onRequestClose={closeMenu}
      >
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onPress={closeMenu}
        >
          <View style={{ flex: 1, justifyContent: 'flex-end' }}>
            {/* Menu Container */}
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={{
                backgroundColor: colors.surface,
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                paddingBottom: insets.bottom > 0 ? insets.bottom : 24,
              }}
            >
              {/* Handle bar */}
              <View className="items-center pt-3 pb-4">
                <View
                  className="w-10 h-1 rounded-full"
                  style={{ backgroundColor: colors.border }}
                />
              </View>

              {/* Title */}
              <Text
                className="text-lg font-bold px-6 mb-4"
                style={{ color: colors.text }}
              >
                Toevoegen aan chat
              </Text>

              {/* Options Grid */}
              <View className="flex-row flex-wrap px-4 pb-4">
                {attachmentOptions.map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    onPress={option.onPress}
                    className="w-1/3 items-center py-4"
                    activeOpacity={0.7}
                    accessibilityLabel={option.label}
                    accessibilityRole="button"
                  >
                    <View
                      className="w-14 h-14 rounded-full items-center justify-center mb-2"
                      style={{ backgroundColor: option.color + '20' }}
                    >
                      <Ionicons name={option.icon} size={28} color={option.color} />
                    </View>
                    <Text
                      className="text-sm font-medium"
                      style={{ color: colors.text }}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Cancel button */}
              <TouchableOpacity
                onPress={closeMenu}
                className="mx-4 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
                activeOpacity={0.7}
              >
                <Text
                  className="text-base font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Annuleren
                </Text>
              </TouchableOpacity>
            </Pressable>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};
